/**
 * diagnosticsBuffer.ts
 *
 * Monkey-patches console + fetch/XHR to maintain in-memory ring buffers of
 * console messages and network requests for the current page. Redacts
 * credentials before storing. Clears on route change.
 *
 * Init: call `initDiagnosticsBuffer()` once (idempotent). Only called by
 * GatedFeedbackWidget — normal customers are never patched.
 *
 * Usage: `getDiagnostics(route)` → `{ console, network, capturedForRoute }`.
 * Teardown: `teardownDiagnosticsBuffer()` — restores originals, clears buffers.
 */

// ─── types ────────────────────────────────────────────────────────────────────

export interface ConsoleEntry {
  level: 'log' | 'info' | 'warn' | 'error' | 'debug'
  ts: number
  message: string
}

export interface NetworkEntry {
  method: string
  url: string
  status: number | null
  durationMs: number | null
  ts: number
}

export interface DiagnosticsSnapshot {
  console: ConsoleEntry[]
  network: NetworkEntry[]
  capturedForRoute: string
}

// ─── ring buffer ──────────────────────────────────────────────────────────────

const CONSOLE_CAP = 100
const NETWORK_CAP = 50
const MSG_MAX_CHARS = 500

let consoleBuffer: ConsoleEntry[] = []
let networkBuffer: NetworkEntry[] = []
let currentRoute = ''

function pushConsole(entry: ConsoleEntry): void {
  consoleBuffer.push(entry)
  if (consoleBuffer.length > CONSOLE_CAP) {
    consoleBuffer = consoleBuffer.slice(consoleBuffer.length - CONSOLE_CAP)
  }
}

function pushNetwork(entry: NetworkEntry): void {
  networkBuffer.push(entry)
  if (networkBuffer.length > NETWORK_CAP) {
    networkBuffer = networkBuffer.slice(networkBuffer.length - NETWORK_CAP)
  }
}

// ─── redaction ────────────────────────────────────────────────────────────────

const REDACT_PATTERNS: Array<[RegExp, string]> = [
  [/Bearer\s+\S+/gi, 'Bearer [REDACTED]'],
  [/(token|key|secret|password|authorization)=[^&\s"']+/gi, '$1=[REDACTED]'],
  // Long base64-like runs (≥40 chars of base64 alphabet)
  [/[A-Za-z0-9+/=]{40,}/g, '[REDACTED]'],
]

function redact(s: string): string {
  let out = s
  for (const [re, replacement] of REDACT_PATTERNS) {
    out = out.replace(re, replacement)
  }
  return out
}

function safeStringify(...args: unknown[]): string {
  const parts = args.map((a) => {
    try {
      if (typeof a === 'string') return a
      if (a instanceof Error) return `${a.name}: ${a.message}`
      return JSON.stringify(a)
    } catch {
      return String(a)
    }
  })
  const joined = parts.join(' ')
  return joined.length > MSG_MAX_CHARS ? joined.slice(0, MSG_MAX_CHARS) + '…' : joined
}

// ─── originals ────────────────────────────────────────────────────────────────

type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'
const CONSOLE_LEVELS: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug']

const originalConsole: Record<ConsoleLevel, (...args: unknown[]) => void> = {} as never
const originalFetch: typeof fetch = typeof fetch !== 'undefined' ? fetch : ((() => {}) as never)
const originalXHROpen = typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest.prototype.open : null
const originalXHRSend = typeof XMLHttpRequest !== 'undefined' ? XMLHttpRequest.prototype.send : null

let initialized = false

// ─── console patch ────────────────────────────────────────────────────────────

function patchConsole(): void {
  for (const level of CONSOLE_LEVELS) {
    originalConsole[level] = (console[level] as (...args: unknown[]) => void).bind(console)
    console[level] = (...args: unknown[]) => {
      // Always call through first — real logging must never be lost
      originalConsole[level](...args)
      pushConsole({
        level,
        ts: Date.now(),
        message: redact(safeStringify(...args)),
      })
    }
  }
}

function restoreConsole(): void {
  for (const level of CONSOLE_LEVELS) {
    if (originalConsole[level]) {
      console[level] = originalConsole[level] as typeof console.log
    }
  }
}

// ─── fetch patch ──────────────────────────────────────────────────────────────

function patchFetch(): void {
  if (typeof fetch === 'undefined') return

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method ?? 'GET').toUpperCase()
    let rawUrl: string
    try {
      rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
    } catch {
      rawUrl = String(input)
    }
    const url = redact(rawUrl)
    const ts = Date.now()
    let status: number | null = null
    let durationMs: number | null = null

    try {
      const response = await originalFetch(input, init)
      status = response.status
      durationMs = Date.now() - ts
      pushNetwork({ method, url, status, durationMs, ts })
      return response
    } catch (err) {
      durationMs = Date.now() - ts
      pushNetwork({ method, url, status: null, durationMs, ts })
      throw err
    }
  }
}

function restoreFetch(): void {
  if (typeof fetch !== 'undefined') {
    window.fetch = originalFetch
  }
}

// ─── XHR patch ────────────────────────────────────────────────────────────────

function patchXHR(): void {
  if (!originalXHROpen || !originalXHRSend) return


  ;(XMLHttpRequest.prototype as any).open = function (
    this: XMLHttpRequest & { _diagMethod?: string; _diagUrl?: string; _diagTs?: number },
    method: string,
    url: string | URL,
    asyncFlag?: boolean,
    username?: string,
    password?: string,
  ) {
    this._diagMethod = method.toUpperCase()
    this._diagUrl = redact(typeof url === 'string' ? url : url.toString())
    this._diagTs = Date.now()

    return (originalXHROpen as any).call(this, method, url, asyncFlag, username, password)
  }

  XMLHttpRequest.prototype.send = function (
    this: XMLHttpRequest & { _diagMethod?: string; _diagUrl?: string; _diagTs?: number },
    ...args: Parameters<typeof XMLHttpRequest.prototype.send>
  ) {
    const ts = this._diagTs ?? Date.now()
    const method = this._diagMethod ?? 'GET'
    const url = this._diagUrl ?? ''

    this.addEventListener('loadend', () => {
      pushNetwork({
        method,
        url,
        status: this.status ?? null,
        durationMs: Date.now() - ts,
        ts,
      })
    })


    return (originalXHRSend as any).call(this, ...args)
  }
}

function restoreXHR(): void {
  if (!originalXHROpen || !originalXHRSend) return
  XMLHttpRequest.prototype.open = originalXHROpen
  XMLHttpRequest.prototype.send = originalXHRSend
}

// ─── public API ───────────────────────────────────────────────────────────────

/** Init patches — idempotent; safe to call multiple times. */
export function initDiagnosticsBuffer(): void {
  if (initialized) return
  initialized = true
  patchConsole()
  patchFetch()
  patchXHR()
}

/** Clear buffers when the route changes so each page starts fresh. */
export function onRouteChange(newRoute: string): void {
  consoleBuffer = []
  networkBuffer = []
  currentRoute = newRoute
}

/** Snapshot current buffers for submission. */
export function getDiagnostics(route: string): DiagnosticsSnapshot {
  return {
    console: [...consoleBuffer],
    network: [...networkBuffer],
    capturedForRoute: route,
  }
}

/** Restore all originals + clear buffers. Call in tests or on logout. */
export function teardownDiagnosticsBuffer(): void {
  if (!initialized) return
  initialized = false
  restoreConsole()
  restoreFetch()
  restoreXHR()
  consoleBuffer = []
  networkBuffer = []
  currentRoute = ''
}

/** Read-only: current route being tracked. */
export { currentRoute }
